/**
 * A per-product configurable option returned by `GET /products/:id`
 * (`result.product_config_options[]`). Drives the Mlytics/generic-config
 * template: each option becomes one `service_configoptions[]` entry.
 */
export interface ProductConfigOption {
  product_config_option_id?: number;
  name: string;
  title?: string;
  subtitle?: string;
  type?: string;
  unit?: string;
  value?: number;
  min?: number;
  max?: number;
  price?: number;
  is_editable?: boolean;
  statementable?: boolean;
  product_pricing_steps?: Array<{
    price_start: number;
    price_end: number;
    price_per_unit: number;
  }>;
}

/** Read a product's config options off the loosely-typed product detail. */
export function readProductConfigOptions(detail: unknown): ProductConfigOption[] {
  const raw = (detail as { product_config_options?: unknown } | null | undefined)
    ?.product_config_options;
  return Array.isArray(raw) ? (raw as ProductConfigOption[]) : [];
}

/** Map a product config option → an initial `service_configoptions[]` entry. */
export function toServiceConfigOption(option: ProductConfigOption, prepaid: boolean) {
  const commitmentValue = option.value ?? 0;
  const perUnit = option.price ?? 0;
  const prices = (option.product_pricing_steps ?? [])
    .slice()
    .sort((a, b) => a.price_start - b.price_start)
    .map((step) => ({
      price_start: step.price_start,
      price_end: step.price_end,
      price_per_unit: step.price_per_unit,
    }));
  return {
    name: option.name,
    unit: option.unit,
    type: option.type,
    price_config: { method: 'progressive' },
    commitment_prepaid: prepaid,
    statementable: option.statementable ?? false,
    commitment_value: commitmentValue,
    commitment_price_per_unit: perUnit || undefined,
    commitment_price_total: commitmentValue * perUnit,
    commitment_time: 0,
    resizable: option.is_editable ?? false,
    prices,
  };
}
